import { Box, Button, MenuItem, Modal, Select, TextField, Typography } from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import CheckIcon from '@mui/icons-material/Check'
import { useIntl } from 'react-intl'
import { useForm } from 'react-hook-form'

interface Props {
  open: boolean
  onClose: () => void
  onSubmit: (payload: CognitoUserCreateForm) => void
}

export default function AdminUserCreateModal({ open, onClose, onSubmit }: Props) {
  const intl = useIntl()
  const { register, handleSubmit, setValue, watch } = useForm<CognitoUserCreateForm>({
    defaultValues: {
      email: '',
      phone: '',
      role: '',
    },
  })

  const role = watch('role')

  const submitForm = (data: CognitoUserCreateForm) => {
    onSubmit(data)
  }

  return (
    <>
      <Modal open={open} onClose={onClose}>
        <form onSubmit={handleSubmit(submitForm)}>
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 500,
              bgcolor: '#FFF',
              borderRadius: '12px',
              boxShadow: 24,
              display: 'flex',
              flexDirection: 'column',
              outline: 'none',
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                justifyContent: 'space-between',
                alignItems: 'center',
                alignSelf: 'stretch',
                borderBottom: '1px solid #E4E4E7',
                color: '#022314',
                fontWeight: '500',
                lineHeight: '24px',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>Create User</Typography>
                <Typography sx={{ color: '#6B7280', fontSize: '14px' }}>
                  Configure this user’s verification and sign-in options.
                </Typography>
              </Box>
              <Button
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  color: '#8A8A8E',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  flexShrink: 0,
                  minWidth: 0,
                  background: '#FFF',
                  border: 'solid 1px',
                  borderColor: '#E4E4E7',
                }}
                onClick={onClose}
              >
                <ClearIcon sx={{ fontSize: '18px', fontWeight: 400, lineHeight: 'normal' }} />
              </Button>
            </Box>

            {/* Body */}
            <Box
              sx={{
                display: 'flex',
                padding: '20px 22px',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '17px',
                alignSelf: 'stretch',
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  Email address
                </Typography>
                <TextField
                  type="email"
                  placeholder="name@eggsample.com"
                  required
                  {...register('email')}
                  sx={{
                    width: '100%',
                    padding: '10px, 12px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '40px',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      '& .MuiInputBase-root': {
                        width: '100%',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  Phone Number (Optional)
                </Typography>
                <TextField
                  type="tel"
                  placeholder="+358..."
                  {...register('phone')}
                  sx={{
                    width: '100%',
                    padding: '10px, 12px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '40px',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      '& .MuiInputBase-root': {
                        width: '100%',
                      },
                    },
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  Role
                </Typography>
                <Select
                  value={role}
                  {...register('role')}
                  onChange={(e) =>
                    setValue('role', e.target.value, {
                      shouldValidate: true,
                    })
                  }
                  sx={{
                    display: 'flex',
                    height: '40px',
                    borderRadius: '10px',
                  }}
                  required
                  renderValue={(selected) => {
                    if (!selected) {
                      return <span style={{ color: '#8A8A8E' }}>Select role</span>
                    }

                    if (selected === 'adminCourseEditor') {
                      return intl.formatMessage({ id: 'strings.courseEditorAdmin' })
                    }
                    if (selected === 'courseEditor') {
                      return intl.formatMessage({ id: 'strings.courseEditor' })
                    }

                    return selected
                  }}
                  displayEmpty
                >
                  <MenuItem
                    value="adminCourseEditor"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <span>{intl.formatMessage({ id: 'strings.courseEditorAdmin' })}</span>
                    {role === 'adminCourseEditor' && <CheckIcon sx={{ fontSize: '20px', color: '#0BB163' }} />}
                  </MenuItem>
                  <MenuItem
                    value="courseEditor"
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    <span>{intl.formatMessage({ id: 'strings.courseEditor' })}</span>
                    {role === 'courseEditor' && <CheckIcon sx={{ fontSize: '20px', color: '#0BB163' }} />}
                  </MenuItem>
                </Select>
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '6px', alignSelf: 'stretch' }}>
                <Typography
                  sx={{
                    color: '#022314',
                    fontSize: '14px',
                    fontWeight: 500,
                    lineHeight: '20px',
                  }}
                >
                  User ID
                </Typography>
                <TextField
                  required
                  type="number"
                  {...register('userId')}
                  placeholder="GGB user id"
                  sx={{
                    width: '100%',
                    padding: '10px, 12px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '10px',
                      height: '40px',
                      boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                      '& .MuiInputBase-root': {
                        width: '100%',
                      },
                    },
                  }}
                />
              </Box>
            </Box>
            {/* Footer */}

            <Box
              sx={{
                display: 'flex',
                padding: '16px 20px',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: '10px',
                alignSelf: 'stretch',
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  border: '1px solid #E4E4E7',
                  background: '#FAFAFA',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  color: '#022314',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                Cancel
              </Button>

              <Button
                type="submit"
                variant="contained"
                sx={{
                  minWidth: 'auto',
                  borderRadius: '10px',
                  background: '#0BB163',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.10)',
                  display: 'flex',
                  height: '36px',
                  padding: '8px 12px',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '10px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  '&:hover': {
                    background: '#099856',
                  },
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </form>
      </Modal>
    </>
  )
}
